import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { getOrders } from '../app/api';
import { useTranslation } from '../app/LanguageContext';
import { formatDateTime } from '../app/date';

const statusLabels = {
  DRAFT: 'orders.draft',
  SENT: 'orders.sent',
  APPROVED: 'orders.approved',
  CONFIRMED: 'orders.confirmed',
  IN_PREPARATION: 'orders.in_preparation',
  DELIVERED: 'orders.delivered',
  REJECTED: 'orders.rejected',
  EXPIRED: 'orders.expired',
  CANCELLED: 'orders.cancelled',
};

const statusColors = {
  DRAFT: 'default',
  SENT: 'info',
  APPROVED: 'success',
  CONFIRMED: 'success',
  IN_PREPARATION: 'info',
  DELIVERED: 'primary',
  REJECTED: 'error',
  EXPIRED: 'warning',
  CANCELLED: 'error',
};

const OrdersPage = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await getOrders();
      setOrders(response.data?.orders || []);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const filteredOrders = statusFilter
    ? orders.filter((o) => o.status === statusFilter)
    : orders;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1">
          {translate('orders.title')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{translate('orders.status')}</InputLabel>
            <Select
              value={statusFilter}
              label={translate('orders.status')}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">{translate('common.all')}</MenuItem>
              {Object.keys(statusLabels).map((s) => (
                <MenuItem key={s} value={s}>
                  {translate(statusLabels[s])}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/orders/new')}>
            {translate('common.new')} {translate('orders.title')}
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{translate('orders.number')}</TableCell>
              <TableCell>{translate('orders.order_title')}</TableCell>
              <TableCell>{translate('orders.customer_id')}</TableCell>
              <TableCell>{translate('orders.status')}</TableCell>
              <TableCell>{translate('orders.created_at')}</TableCell>
              <TableCell align="right">{translate('orders.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {translate('common.loading')}
                </TableCell>
              </TableRow>
            ) : filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {translate('orders.no_orders')}
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders.map((o) => (
                <TableRow key={o.id}>
                  <TableCell>{o.number}</TableCell>
                  <TableCell>{o.title || '—'}</TableCell>
                  <TableCell>{o.customer_name || o.customer_id || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={translate(statusLabels[o.status] || 'orders.status')}
                      color={statusColors[o.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(o.created_at) || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => navigate(`/orders/${o.id}`)} title={translate('common.view')}>
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => navigate(`/orders/${o.id}/edit`)} title={translate('common.edit')}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrdersPage;
